import React, { useState, useCallback, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
import { DeviceTypes, InputForm } from '../../../types';
import { Team } from '../../../types/teams.types';
import { Group } from '../../../types/groups.types';
import { Tag } from '../../../types/tags.types';
import { UnifiedFilterBar, FilterField, DateRangeValue, DATE_RANGE_PERIOD_OPTIONS } from '../../shared/UnifiedFilterBar';
import { SHIPPED_FIELDS } from '../../../builders/devices-builders';
import { displayName } from '../../../helpers/displayName';

interface Props {
    onFilter(filters: DeviceTypes.Filters): Promise<void>;
    onDownload(): Promise<void>;
    filterInputs?: InputForm[];
    // Group/Team/Tag scoping — assigned tab only (unassigned devices have no
    // user to scope by). Omitting `showScopeFilters` hides the pickers.
    showScopeFilters?: boolean;
    // Assigned tab only — unassigned devices have no user, so the API
    // ignores a name filter there and a User Name search can never match.
    // When hidden, Shipped To takes its slot as the person search.
    showUserNameFilter?: boolean;
    // Mirrors the hideLastPing customer setting: when the Last Update column
    // is hidden, don't offer a filter on it either.
    showLastUpdateFilter?: boolean;
    allTeams?: Team[];
    allGroups?: Group[];
    allTags?: Tag[];
}

// Values must match the API's returnStatus enum (not_set/not_returned/...) —
// the previous display-text values ('Not Returned') never matched anything.
const RETURN_STATUS_OPTIONS = [
    { label: 'Not Set', value: 'not_set' },
    { label: 'Not Returned', value: 'not_returned' },
    { label: 'Returned', value: 'returned' },
    { label: 'In Transit', value: 'in_transit' },
];

const RETURN_STATUS_LABELS: Record<string, string> = Object.fromEntries(
    RETURN_STATUS_OPTIONS.map((o) => [o.value, o.label]),
);

// Shipping-manifest snapshot filters (unassigned tab) — derived from the
// same SHIPPED_FIELDS source as the table columns, so the filter panel,
// pills, and column headers can't drift apart. Free-text partial match,
// same contract as assetTag.
const SHIPPED_FILTER_FIELDS = SHIPPED_FIELDS.map((f) => ({
    key: f.field, label: f.headerName, placeholder: f.placeholder,
}));

// Promoted to the primary row on the unassigned tab (no user → no User
// Name search); the remaining shipped fields stay in the expanded panel.
// Null if the field is ever removed from SHIPPED_FIELDS — usages guard,
// degrading to no promotion rather than a TypeError at module init.
const SHIPPED_TO_FIELD = SHIPPED_FILTER_FIELDS.find((f) => f.key === 'shippedTo') ?? null;

const EMPTY_RANGE: DateRangeValue = { period: '', from: null, to: null };

const PERIOD_LABELS: Record<string, string> = Object.fromEntries(
    DATE_RANGE_PERIOD_OPTIONS.map((o) => [o.value, o.label]),
);

const PERIOD_DAYS: Record<string, number> = { '7d': 7, '14d': 14, '30d': 30, '90d': 90 };

type Values = {
    name: string;
    serialNumber: string;
    assetTag: string;
    shippedTo: string;
    shippedToEmployeeId: string;
    shippedToGroup: string;
    shippedToTeam: string;
    returnStatus: string;
    groupIds: string[];
    teamIds: string[];
    tags: string[];
    // Two independent date ranges, keyed and labeled to match the table's
    // column headers (Created At / Last Update). Each is a preset period or
    // a custom From/To pair.
    createdAtRange: DateRangeValue;
    updatedAtRange: DateRangeValue;
};

const buildEmpty = (): Values => ({
    name: '', serialNumber: '', assetTag: '', returnStatus: '',
    shippedTo: '', shippedToEmployeeId: '', shippedToGroup: '', shippedToTeam: '',
    groupIds: [], teamIds: [], tags: [],
    createdAtRange: { ...EMPTY_RANGE }, updatedAtRange: { ...EMPTY_RANGE },
});

// Custom ranges need both valid dates in order; presets and "All" are
// always valid.
const rangeIsValid = (r: DateRangeValue): boolean =>
    r.period !== 'custom' ||
    Boolean(r.from && r.to && r.from.isValid() && r.to.isValid() && !r.from.isAfter(r.to));

// Resolve the range to day-bounded ISO timestamps in the viewer's timezone —
// the API applies them verbatim ($gte/$lte). Presets resolve relative to
// "now" at Apply time, mirroring the page-level Time Period control.
const rangeToApi = (r: DateRangeValue): { from?: string; to?: string } => {
    const days = PERIOD_DAYS[r.period];
    if (days !== undefined) {
        return {
            from: dayjs().subtract(days, 'day').startOf('day').toISOString(),
            to: dayjs().endOf('day').toISOString(),
        };
    }
    if (r.period === 'custom' && r.from && r.to && r.from.isValid() && r.to.isValid()) {
        return {
            from: r.from.startOf('day').toISOString(),
            to: r.to.endOf('day').toISOString(),
        };
    }
    return {};
};

const rangePillLabel = (r: DateRangeValue): string | null => {
    if (!r.period) return null;
    if (r.period === 'custom') {
        if (!r.from || !r.to) return null;
        const sameYear = r.from.year() === r.to.year();
        const startFmt = sameYear ? r.from.format('MMM D') : r.from.format('MMM D, YYYY');
        return `${startFmt} – ${r.to.format('MMM D, YYYY')}`;
    }
    return PERIOD_LABELS[r.period] ?? r.period;
};

export const DeviceListFilters: React.FC<Props> = ({
    onFilter, onDownload, filterInputs, showScopeFilters, showLastUpdateFilter = true,
    showUserNameFilter = true,
    allTeams = [], allGroups = [], allTags = [],
}) => {
    const hasAssetTag = !!filterInputs?.some((f) => f.id === 'assetTag');
    const hasReturnStatus = !!filterInputs?.some((f) => f.id === 'returnStatus');
    // The builders add all four shipped inputs together, so one id implies all.
    const hasShippedFields = !!filterInputs?.some((f) => f.id === 'shippedTo');

    const [values, setValues] = useState<Values>(buildEmpty);
    const [applied, setApplied] = useState<Values>(buildEmpty);

    // Build filter fields based on what filterInputs requests
    const primaryFilters: FilterField[] = useMemo(() => {
        const fields: FilterField[] = [];
        if (showUserNameFilter) {
            fields.push({ key: 'name', label: 'User Name', type: 'text', placeholder: 'Search by name...' });
        } else if (hasShippedFields && SHIPPED_TO_FIELD) {
            // Unassigned tab's person search — the manifest's shipped-to
            // name stands in for the user the device has no link to.
            fields.push({
                key: SHIPPED_TO_FIELD.key, label: SHIPPED_TO_FIELD.label,
                type: 'text', placeholder: SHIPPED_TO_FIELD.placeholder,
            });
        }
        fields.push({ key: 'serialNumber', label: 'Serial Number', type: 'text', placeholder: 'Search by serial...' });
        if (hasAssetTag) {
            fields.push({ key: 'assetTag', label: 'Asset Tag', type: 'text', placeholder: 'Search by asset tag...' });
        }
        if (hasReturnStatus) {
            fields.push({ key: 'returnStatus', label: 'Return Status', type: 'select', options: RETURN_STATUS_OPTIONS });
        }
        return fields;
    }, [showUserNameFilter, hasShippedFields, hasAssetTag, hasReturnStatus]);

    // When groups are selected, narrow the visible team list to only teams
    // belonging to the selected group(s) — same UX as the POM Users filters.
    const visibleTeams = useMemo(() => {
        if (values.groupIds.length === 0) return allTeams;
        const groupSet = new Set(values.groupIds);
        return allTeams.filter((t) => t.groupId && groupSet.has(t.groupId));
    }, [allTeams, values.groupIds]);

    const groupNameByTeamId = useMemo(() => {
        const m = new Map<string, string>();
        const groupNameById = new Map<string, string>();
        for (const g of allGroups) groupNameById.set(g.id || (g as any)._id || '', g.name);
        for (const t of allTeams) {
            const tid = t.id || (t as any)._id;
            const gid = t.groupId;
            if (tid && gid && groupNameById.has(gid)) {
                m.set(tid, groupNameById.get(gid) as string);
            }
        }
        return m;
    }, [allTeams, allGroups]);

    const expandedFilters: FilterField[] = useMemo(() => {
        const fields: FilterField[] = [
            // Labels match the table's column headers on purpose — the
            // filter and the column it filters read identically.
            { key: 'createdAtRange', label: 'Created At', type: 'date-range' },
            ...(showLastUpdateFilter
                ? [{ key: 'updatedAtRange', label: 'Last Update', type: 'date-range' as const }]
                : []),
        ];
        if (hasShippedFields) {
            // Shipped To sits in the primary row when User Name is hidden
            // (unassigned tab) — don't render it twice. When SHIPPED_TO_FIELD
            // is null nothing was promoted, so nothing is excluded here.
            fields.push(...SHIPPED_FILTER_FIELDS
                .filter((f) => showUserNameFilter || f.key !== SHIPPED_TO_FIELD?.key)
                .map((f) => ({
                    key: f.key, label: f.label, type: 'text' as const, placeholder: f.placeholder,
                })));
        }
        if (showScopeFilters) {
            fields.push(
                {
                    key: 'groupIds',
                    label: 'Group',
                    type: 'grouped-multiselect',
                    placeholder: 'All groups',
                    options: allGroups.map((g) => ({
                        label: g.name || '',
                        value: g.id || (g as any)._id || '',
                    })),
                },
                {
                    key: 'teamIds',
                    label: 'Team',
                    type: 'grouped-multiselect',
                    placeholder: values.groupIds.length > 0 ? 'Teams in selected group(s)' : 'All teams',
                    options: visibleTeams.map((t) => {
                        const tid = t.id || (t as any)._id || '';
                        return {
                            label: t.name || '',
                            value: tid,
                            group: values.groupIds.length === 0 ? groupNameByTeamId.get(tid) : undefined,
                        };
                    }),
                },
                {
                    key: 'tags',
                    label: 'User Tags',
                    type: 'tag-picker',
                    placeholder: 'User tags…',
                    tagOptions: allTags.map((t) => ({
                        id: t._id || '',
                        name: t.name,
                    })),
                },
            );
        }
        return fields;
    }, [showLastUpdateFilter, showScopeFilters, showUserNameFilter, hasShippedFields, allGroups, visibleTeams, allTags, groupNameByTeamId, values.groupIds]);

    // Group selection narrows the team list — selected teams outside the
    // chosen group(s) must be dropped, or they'd keep being sent to the API
    // (which intersects groups∩teams server-side → silent empty list) while
    // the Team picker can't even display them. Shared by the picker path
    // (handleChange) and the pill-removal path so the two can't diverge.
    const narrowTeamsToGroups = useCallback((groupIds: string[], teamIds: string[]): string[] => {
        if (groupIds.length === 0) return teamIds;
        const groupSet = new Set(groupIds);
        const visibleTeamIds = new Set(
            allTeams
                .filter((t) => t.groupId && groupSet.has(t.groupId))
                .map((t) => t.id || (t as any)._id)
        );
        return teamIds.filter((tid) => visibleTeamIds.has(tid));
    }, [allTeams]);

    const handleChange = useCallback((key: string, value: any) => {
        setValues((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'groupIds') {
                next.teamIds = narrowTeamsToGroups(value as string[], prev.teamIds);
            }
            return next;
        });
    }, [narrowTeamsToGroups]);

    const dispatch = useCallback((next: Values) => {
        const created = rangeToApi(next.createdAtRange);
        const updated = rangeToApi(next.updatedAtRange);
        const filters = {
            name: next.name,
            serialNumber: next.serialNumber,
            ...(next.assetTag ? { assetTag: next.assetTag } : {}),
            ...(next.returnStatus ? { returnStatus: next.returnStatus } : {}),
            ...Object.fromEntries(
                SHIPPED_FILTER_FIELDS.filter((f) => next[f.key]).map((f) => [f.key, next[f.key]]),
            ),
            ...(showScopeFilters ? {
                groupIds: next.groupIds,
                teamIds: next.teamIds,
                tags: next.tags,
            } : {}),
            ...(created.from ? { createdAtFrom: created.from } : {}),
            ...(created.to ? { createdAtTo: created.to } : {}),
            ...(updated.from ? { updatedAtFrom: updated.from } : {}),
            ...(updated.to ? { updatedAtTo: updated.to } : {}),
        } as DeviceTypes.Filters;
        onFilter(filters).catch((err) => {
            console.error('[DeviceListFilters] onFilter failed', err);
        });
    }, [onFilter, showScopeFilters]);

    const handleApply = useCallback(() => {
        if (!rangeIsValid(values.createdAtRange) || !rangeIsValid(values.updatedAtRange)) {
            toast('Pick a start and end date (in order) for the custom range.', { type: 'warning', autoClose: 2500 });
            return;
        }
        setApplied(values);
        dispatch(values);
    }, [values, dispatch]);

    const handleClear = useCallback(() => {
        const cleared = buildEmpty();
        setValues(cleared);
        setApplied(cleared);
        dispatch(cleared);
    }, [dispatch]);

    const removeApplied = useCallback((next: Values) => {
        setValues(next);
        setApplied(next);
        dispatch(next);
    }, [dispatch]);

    // Team names come from both stores: GroupService.fetchAll overwrites the
    // teams slice with only its group-nested teams, so whichever fetch landed
    // last decides what `allTeams` holds. Reading group-nested teams too makes
    // the pill label independent of that race.
    const teamNameById = useMemo(() => {
        const m = new Map<string, string>();
        const add = (t: any) => {
            const id = t?.id || t?._id;
            // Gate on the name, not the key: a nameless entry in the first source
            // must not block a named copy of the same team in the second.
            if (id && t?.name && !m.get(id)) m.set(id, t.name);
        };
        for (const t of allTeams) add(t);
        for (const g of allGroups) for (const t of (g.teams || [])) add(t);
        return m;
    }, [allTeams, allGroups]);

    const groupNameById = useMemo(() => {
        const m = new Map<string, string>();
        for (const g of allGroups) m.set(g.id || (g as any)._id || '', g.name);
        return m;
    }, [allGroups]);

    const tagNameById = useMemo(() => {
        const m = new Map<string, string>();
        for (const t of allTags) m.set(t._id || '', t.name);
        return m;
    }, [allTags]);

    const appliedFilters = useMemo(() => {
        const pills: { key: string; label: string; onRemove: () => void }[] = [];
        if (applied.name) pills.push({
            key: 'name', label: `User Name: ${applied.name}`,
            onRemove: () => removeApplied({ ...applied, name: '' }),
        });
        if (applied.serialNumber) pills.push({
            key: 'serialNumber', label: `Serial: ${applied.serialNumber}`,
            onRemove: () => removeApplied({ ...applied, serialNumber: '' }),
        });
        if (applied.assetTag) pills.push({
            key: 'assetTag', label: `Asset Tag: ${applied.assetTag}`,
            onRemove: () => removeApplied({ ...applied, assetTag: '' }),
        });
        if (applied.returnStatus) pills.push({
            key: 'returnStatus',
            label: `Return Status: ${RETURN_STATUS_LABELS[applied.returnStatus] ?? applied.returnStatus}`,
            onRemove: () => removeApplied({ ...applied, returnStatus: '' }),
        });
        for (const f of SHIPPED_FILTER_FIELDS) {
            if (applied[f.key]) pills.push({
                key: f.key,
                label: `${f.label}: ${applied[f.key]}`,
                onRemove: () => removeApplied({ ...applied, [f.key]: '' }),
            });
        }
        for (const groupId of applied.groupIds) {
            const name = displayName(groupNameById.get(groupId));
            pills.push({
                key: `group:${groupId}`,
                label: `Group: ${name}`,
                onRemove: () => {
                    // Re-narrow teams against the remaining groups — same
                    // rule as picking groups in the dropdown.
                    const nextGroupIds = applied.groupIds.filter((g) => g !== groupId);
                    removeApplied({
                        ...applied,
                        groupIds: nextGroupIds,
                        teamIds: narrowTeamsToGroups(nextGroupIds, applied.teamIds),
                    });
                },
            });
        }
        for (const teamId of applied.teamIds) {
            const name = displayName(teamNameById.get(teamId));
            pills.push({
                key: `team:${teamId}`,
                label: `Team: ${name}`,
                onRemove: () => removeApplied({ ...applied, teamIds: applied.teamIds.filter((t) => t !== teamId) }),
            });
        }
        for (const tagId of applied.tags) {
            const name = displayName(tagNameById.get(tagId));
            pills.push({
                key: `tag:${tagId}`,
                label: `Tag: ${name}`,
                onRemove: () => removeApplied({ ...applied, tags: applied.tags.filter((t) => t !== tagId) }),
            });
        }
        const createdLabel = rangePillLabel(applied.createdAtRange);
        if (createdLabel) pills.push({
            key: 'createdAtRange',
            label: `Created At: ${createdLabel}`,
            onRemove: () => removeApplied({ ...applied, createdAtRange: { ...EMPTY_RANGE } }),
        });
        const updatedLabel = rangePillLabel(applied.updatedAtRange);
        if (updatedLabel) pills.push({
            key: 'updatedAtRange',
            label: `Last Update: ${updatedLabel}`,
            onRemove: () => removeApplied({ ...applied, updatedAtRange: { ...EMPTY_RANGE } }),
        });
        return pills;
    }, [applied, teamNameById, groupNameById, tagNameById, removeApplied, narrowTeamsToGroups]);

    return (
        <UnifiedFilterBar
            primaryFilters={primaryFilters}
            expandedFilters={expandedFilters}
            values={values}
            appliedFilters={appliedFilters}
            onChange={handleChange}
            onApply={handleApply}
            onClear={handleClear}
            onExport={onDownload}
        />
    );
};
